import { AxiosError } from 'axios';

/**
 * Lỗi parse từ envelope BE chuẩn API-CONTRACT §1.4:
 *   { success: false, error: { code, message, details? } }
 *
 * @see ERP-docs/technical/api/API-CONTRACT.md §5
 */
export class ApiError extends Error {
  readonly code: string;
  readonly status: number;
  readonly details?: Record<string, unknown>;

  constructor(
    code: string,
    message: string,
    status: number,
    details?: Record<string, unknown>,
  ) {
    super(message);
    this.name = 'ApiError';
    this.code = code;
    this.status = status;
    this.details = details;
  }

  /**
   * Trả về danh sách lỗi validation từ details.errors[] (nếu có) hoặc rỗng.
   */
  get validationErrors(): Array<{ field: string; constraint: string; message: string }> {
    const errs = this.details?.errors;
    return Array.isArray(errs) ? (errs as Array<{ field: string; constraint: string; message: string }>) : [];
  }
}

interface ApiErrorEnvelope {
  success: false;
  error: { code: string; message: string; details?: Record<string, unknown> };
}

function isApiErrorEnvelope(value: unknown): value is ApiErrorEnvelope {
  if (!value || typeof value !== 'object') return false;
  const v = value as Record<string, unknown>;
  if (v.success !== false) return false;
  const err = v.error as Record<string, unknown> | undefined;
  return (
    !!err &&
    typeof err === 'object' &&
    typeof err.code === 'string' &&
    typeof err.message === 'string'
  );
}

/**
 * Convert AxiosError → ApiError nếu response body match envelope BE chuẩn.
 * Nếu không match (network error, timeout, body khác shape), trả về ApiError với code 'NETWORK_ERROR' / 'UNKNOWN_ERROR'.
 */
export function toApiError(err: unknown): ApiError {
  if (err instanceof ApiError) return err;

  if (err && typeof err === 'object' && 'isAxiosError' in err) {
    const axiosErr = err as AxiosError;
    const status = axiosErr.response?.status ?? 0;
    const data = axiosErr.response?.data;

    if (isApiErrorEnvelope(data)) {
      return new ApiError(
        data.error.code,
        data.error.message,
        status,
        data.error.details,
      );
    }

    if (!axiosErr.response) {
      return new ApiError(
        'NETWORK_ERROR',
        axiosErr.message || 'Mất kết nối mạng',
        0,
      );
    }

    return new ApiError(
      'UNKNOWN_ERROR',
      typeof data === 'string' ? data : axiosErr.message,
      status,
    );
  }

  if (err instanceof Error) {
    return new ApiError('UNKNOWN_ERROR', err.message, 0);
  }

  return new ApiError('UNKNOWN_ERROR', String(err), 0);
}
